/**
 * Research (M9, Track 8): RP income and sources, parallel slots, queue with ETA, laboratories,
 * tech cards that say what they unlock, discoveries and the research log.
 * The server decides availability, price and timing; this view only shows it. Touch targets are >= 44 px.
 */
import { useCallback, useEffect, useState } from 'react';
import { formatGameDuration, formatEta, type ResearchState, type TechBranch } from '@galaxis/shared';
import { fetchResearch, startResearch, cancelResearch, buildLab, upgradeLab, fetchResources } from '../api/galaxy.ts';
import { useGameStore } from '../store/game-store.ts';
import { syncResources } from '../economy/resource-sync.ts';
import { previewResourceSpend, useLiveResources } from '../economy/live-store.ts';

const BRANCHES: Array<{ id: TechBranch; label: string; icon: string }> = [
  { id: 'science', label: 'Tudomány', icon: '🧪' },
  { id: 'economy', label: 'Gazdaság', icon: '⚙️' },
  { id: 'military', label: 'Haditechnika', icon: '⚔️' },
  { id: 'exploration', label: 'Felderítés', icon: '🔭' },
  { id: 'diplomacy', label: 'Diplomácia', icon: '🤝' },
];

const fmt = (ms: number) => (ms <= 0 ? 'kész' : formatGameDuration(ms));
const num = (n: number) => (Math.round(n * 10) / 10).toString().replace('.', ',');
const BTN = 'btn-primary min-h-11 w-full text-xs disabled:opacity-40';
const BTN_GHOST = 'min-h-11 px-3 rounded border border-slate-600 text-xs text-slate-300 hover:border-slate-400 disabled:opacity-40';

export function ResearchView() {
  const [state, setState] = useState<ResearchState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const version = useGameStore(s => s.researchVersion);
  const { pushToast, setResources } = useGameStore();
  const live = useLiveResources();

  const load = useCallback(() => {
    fetchResearch().then(s => { setState(s); setError(null); }).catch(e => setError(e instanceof Error ? e.message : 'Hiba'));
  }, []);
  useEffect(() => { load(); }, [load, version]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // Safety net if a completion push is missed
  const nextDue = state ? Math.min(Infinity, ...state.active.map(a => new Date(a.completesAt).getTime()), ...state.labs.flatMap(l => l.completesAt ? [new Date(l.completesAt).getTime()] : [])) : Infinity;
  const due = nextDue <= now;
  useEffect(() => { if (due) { const t = setTimeout(load, 1500); return () => clearTimeout(t); } }, [due, load]);

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    try {
      const msg = await fn();
      if (msg) pushToast(msg);
      setResources(await fetchResources());
      load();
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'A művelet nem sikerült', 'error');
      void syncResources(); // a previewed cost that was not charged goes back to the server's number
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="flex-1 p-6 text-red-400 text-sm">{error}</div>;
  if (!state) return <div className="flex-1 p-6 text-slate-500 text-sm">Kutatás betöltése…</div>;
  const names = new Map(state.techs.map(t => [t.id, t.name]));
  const byId = new Map(state.techs.map(t => [t.id, t]));
  const freeSlot = state.slots.used < state.slots.total;
  const queueFull = state.queue.length >= state.queueLimit;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="research-view">
      <div className="max-w-6xl mx-auto space-y-4">
        <h1 className="text-lg font-semibold text-slate-200">Kutatás</h1>

        {/* RP income, sources, slots */}
        <section className="sci-fi-panel rounded-xl p-4" aria-label="Kutatási pontok" data-testid="rp-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Kutatási pont (RP)" value={`${Math.floor(state.rp.balance)}`} testId="rp-balance" />
            <Stat label="Bevétel" value={`+${num(state.rp.perHour)} RP/ó`} testId="rp-income" accent />
            <Stat label="Kutatási helyek" value={`${state.slots.used} / ${state.slots.total}`} testId="slots" />
            <Stat label="Labor-fenntartás" value={`−${state.rp.energyUpkeepPerHour} energia/ó`} testId="lab-upkeep" />
          </div>
          <ul className="mt-3 space-y-1 text-xs text-slate-400" data-testid="rp-sources">
            {state.rp.sources.map(s => (
              <li key={s.label} className="flex justify-between gap-2"><span>{s.label}</span><span className="font-mono text-slate-300">+{num(s.perHour)} RP/ó</span></li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-slate-500">
            Helyek: {state.slots.sources.map(s => `${s.label} +${s.count}`).join(' · ')}
            {state.rp.queuedCost > 0 && <> · A sorban álló projektek összesen {state.rp.queuedCost} RP-be kerülnek</>}
          </p>
        </section>

        {/* Running projects */}
        <section aria-label="Futó kutatások" data-testid="active-research" className="space-y-2">
          {state.active.length === 0 && <p className="text-sm text-slate-500">Nincs folyamatban kutatás. Válassz egy elérhető technológiát.</p>}
          {state.active.map(a => {
            const t = byId.get(a.techId);
            const left = new Date(a.completesAt).getTime() - now;
            const pct = Math.min(100, 100 * (now - new Date(a.startedAt).getTime()) / Math.max(1, new Date(a.completesAt).getTime() - new Date(a.startedAt).getTime()));
            return (
              <div key={a.techId} className="sci-fi-panel rounded-xl p-4" data-testid="current-research">
                <div className="flex justify-between items-center gap-2 text-sm">
                  <span className="text-slate-200">Folyamatban: <b>{t?.name ?? a.techId}</b></span>
                  <span className="font-mono text-accent" data-testid="research-eta">{left <= 0 ? 'kész' : formatEta(a.completesAt, now)}</span>
                </div>
                <div className="mt-2 h-2 rounded bg-slate-800 overflow-hidden"><div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} /></div>
                <div className="mt-2 flex justify-end">
                  <button type="button" className={BTN_GHOST} disabled={busy} data-testid={`cancel-${a.techId}`}
                    title={a.refundable ? 'A kifizetett költség fele visszajár' : 'Nincs visszatérítés'}
                    onClick={() => void run(async () => { const r = await cancelResearch(a.techId); return `Kutatás megszakítva (vissza: ${r.refunded.metal} fém, ${r.refunded.energy} energia, ${r.refunded.rp} RP)`; })}>
                    Megszakítás{a.refundable ? ' (50% vissza)' : ''}
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        {/* Queue */}
        {state.queue.length > 0 && (
          <section className="sci-fi-panel rounded-xl p-4" aria-label="Várólista" data-testid="research-queue">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Várólista ({state.queue.length}/{state.queueLimit})</h2>
            <ol className="space-y-2">
              {state.queue.map(q => (
                <li key={q.techId} className="flex items-center justify-between gap-2 text-sm" data-testid={`queued-${q.techId}`}>
                  <span className="text-slate-200">{q.position}. {names.get(q.techId) ?? q.techId}
                    <span className="block text-[11px] text-slate-500">
                      {q.blockedBy ? `Vár: ${q.blockedBy}` : `Indul, ha felszabadul egy hely (kb. ${fmt(Math.max(0, Math.min(...state.active.map(a => new Date(a.completesAt).getTime())) - now))} múlva), ${byId.get(q.techId)?.cost.rp ?? 0} RP + nyersanyag szükséges`}
                    </span>
                  </span>
                  <button type="button" className={BTN_GHOST} disabled={busy} onClick={() => void run(async () => { await cancelResearch(q.techId); return 'Törölve a sorból'; })}>Törlés</button>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Labs */}
        <section className="sci-fi-panel rounded-xl p-4" aria-label="Laboratóriumok" data-testid="labs">
          <h2 className="text-sm font-semibold text-slate-300 mb-2">Laboratóriumok ({state.labs.length})</h2>
          {state.labs.length === 0 && <p className="text-xs text-slate-500 mb-2">Még nincs laboratóriumod: az anyabolygó tudósai csak {num(state.rp.sources[0]?.perHour ?? 1)} RP/órát adnak.</p>}
          <ul className="space-y-2">
            {state.labs.map(l => (
              <li key={l.id} className="rounded-lg border border-slate-700 p-3" data-testid={`lab-${l.systemName}`}>
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-slate-200">{l.systemName} · {l.tier}. szint</span>
                  <span className="text-xs text-slate-400">
                    {l.status === 'building' && l.completesAt ? `Épül: ${formatEta(l.completesAt, now)}` : l.status === 'upgrading' && l.completesAt ? `Fejlesztés: ${formatEta(l.completesAt, now)}` : `+${num(l.rpPerHour)} RP/ó`}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">Fenntartás: {l.energyUpkeep} energia/ó</p>
                {l.next && (
                  <div className="mt-2">
                    <button type="button" className={BTN} data-testid={`upgrade-lab-${l.id}`}
                      disabled={busy || !!l.next.blockedBy || !live || live.metal < l.next.metal || live.energy < l.next.energy}
                      title={l.next.blockedBy ? `Előbb kell: ${l.next.blockedBy}` : undefined}
                      onClick={() => void run(async () => { await upgradeLab(l.id); return `Laboratórium fejlesztése: ${l.next!.tier}. szint`; })}>
                      Fejlesztés {l.next.tier}. szintre — {l.next.metal} fém · {l.next.energy} en. · {fmt(l.next.timeMs)} → +{num(l.next.rpPerHour)} RP/ó, fenntartás {l.next.energyUpkeep} en./ó
                    </button>
                    {l.next.blockedBy && <p className="text-[11px] text-amber-400 mt-1">Feltétel: {l.next.blockedBy}</p>}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {state.labBuild.canBuild && (
            <div className="mt-3 space-y-2" data-testid="lab-sites">
              <p className="text-xs text-slate-400">Új laboratórium: {state.labBuild.metal} fém · {state.labBuild.energy} en. · {fmt(state.labBuild.timeMs)} → +{num(state.labBuild.rpPerHour)} RP/ó, fenntartás {state.labBuild.energyUpkeep} en./ó (a megrendeléstől)</p>
              <div className="flex flex-wrap gap-2">
                {state.labSites.map(s => (
                  <button key={s.systemId} type="button" className={BTN_GHOST} disabled={busy || !live || live.metal < state.labBuild.metal || live.energy < state.labBuild.energy}
                    onClick={() => void run(async () => { await buildLab(s.systemId); return `Laboratórium építése: ${s.systemName}`; })}>
                    Építés: {s.systemName}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Tech tree */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="tech-grid">
          {BRANCHES.map(b => (
            <section key={b.id} aria-label={b.label}>
              <h2 className="text-sm font-semibold text-slate-300 mb-2">{b.icon} {b.label}</h2>
              <ul className="space-y-2">
                {state.techs.filter(t => t.branch === b.id).sort((x, y) => x.tier - y.tier).map(t => {
                  const c = t.effectiveCost;
                  const afford = !!live && live.metal >= c.metal && live.energy >= c.energy && state.rp.balance >= c.rp;
                  const tone = t.status === 'done' ? 'border-emerald-500/40' : t.status === 'researching' ? 'border-accent/60' : t.status === 'queued' ? 'border-accent/30' : t.status === 'available' ? 'border-slate-600' : 'border-slate-800 opacity-60';
                  const willQueue = !freeSlot || state.queue.length > 0;
                  return (
                    <li key={t.id} className={`sci-fi-panel rounded-lg p-3 border ${tone}`} data-testid={`tech-${t.id}`} data-status={t.status} data-role={t.role}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-100 font-medium">{t.name}</span>
                        <span className="flex items-center gap-1 text-[10px]">
                          <span className={`px-1.5 py-0.5 rounded ${t.role === 'rule' ? 'bg-accent/20 text-accent' : 'bg-slate-700/60 text-slate-400'}`}>{t.role === 'rule' ? 'Új lehetőség' : 'Támogató'}</span>
                          <span className="text-slate-500">{t.tier}. szint</span>
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{t.description}</p>
                      <ul className="mt-1 text-[11px] text-emerald-300/90 list-disc list-inside" data-testid={`unlocks-${t.id}`}>
                        {t.unlocks.map(u => <li key={u}>{u}</li>)}
                      </ul>
                      {t.discoveryOnly ? (
                        <p className="text-[11px] text-amber-400 mt-1">Csak felfedezéssel szerezhető meg (prekurzor-műtárgy).</p>
                      ) : (
                        <>
                          {t.requires.length > 0 && <p className="text-[11px] text-slate-500 mt-1">Feltétel: {t.requires.map(r => names.get(r) ?? r).join(', ')}</p>}
                          <div className="flex items-center justify-between gap-2 mt-2">
                            <span className="text-[11px] font-mono text-slate-400">
                              {c.rp} RP · {c.metal} fém · {c.energy} en. · {fmt(t.timeMs)}
                              {c.metal !== t.cost.metal && <span className="text-emerald-400"> (roncsmező −50%)</span>}
                            </span>
                            {t.status === 'done' && <span className="text-xs text-emerald-400">✓ Kész</span>}
                            {t.status === 'researching' && <span className="text-xs text-accent">Folyamatban</span>}
                            {t.status === 'queued' && <span className="text-xs text-accent">Sorban: {t.queuePosition}.</span>}
                          </div>
                        </>
                      )}
                      {t.status === 'available' && (
                        <button type="button" className={`${BTN} mt-2`} data-testid={`start-${t.id}`}
                          disabled={busy || (willQueue && queueFull) || (!willQueue && !afford)}
                          title={willQueue ? (queueFull ? 'A várólista megtelt' : 'Minden hely foglalt: sorba kerül') : !afford ? 'Nincs elég RP vagy nyersanyag' : undefined}
                          onClick={() => void run(async () => { if (!willQueue) previewResourceSpend(c.metal, c.energy, c.rp); const r = await startResearch(t.id); return r.status === 'queued' ? `Sorba állítva: ${t.name}` : `Kutatás elindítva: ${t.name}`; })}>
                          {willQueue ? 'Sorba állítás' : 'Kutatás indítása'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        {/* Discoveries + log */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="sci-fi-panel rounded-xl p-4" aria-label="Felfedezések" data-testid="discoveries">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Felfedezések ({state.discoveries.length})</h2>
            {state.discoveries.length === 0 && <p className="text-xs text-slate-500">Felmért rendszerekben leletekre bukkanhatsz: adattár (RP), roncsmező (kedvezmény), prekurzor-műtárgy (egyedi technológia).</p>}
            <ul className="space-y-2">
              {state.discoveries.map(d => (
                <li key={d.id} className="text-xs"><b className="text-slate-200">{d.name}</b> — {d.systemName}<br /><span className="text-accent">{d.reward}</span>{d.used && <span className="text-slate-500"> · felhasználva</span>}</li>
              ))}
            </ul>
          </section>
          <section className="sci-fi-panel rounded-xl p-4" aria-label="Kutatási napló" data-testid="research-log">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Kutatási napló</h2>
            {state.log.length === 0 && <p className="text-xs text-slate-500">Még nincs bejegyzés.</p>}
            <ul className="space-y-1 max-h-56 overflow-y-auto">
              {state.log.map((l, i) => (
                <li key={`${l.at}-${i}`} className="text-xs text-slate-400"><span className="text-slate-600">{new Date(l.at).toLocaleString('hu-HU')}</span> · {l.text}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testId, accent }: { label: string; value: string; testId: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`font-mono text-base ${accent ? 'text-accent' : 'text-slate-100'}`} data-testid={testId}>{value}</div>
    </div>
  );
}
